import en from './translations/en-GB.json';
import moment from 'moment';
import {
  FormattedDate,
  FormattedMessage,
  IntlFormatters,
  useIntl,
} from 'react-intl';
import { Props as BaseFormattedMessageProps } from 'react-intl/src/components/message';

type Translations = typeof en;
export type TranslationsKeys = keyof Translations;

let RESOLVED_MOMENT_LOCALE: string;

const importLocale = async (code: string) => {
  const momentLocale = await import(
    `../../node_modules/moment/dist/locale/${code}.js`
  );
  moment.defineLocale(code, momentLocale.default._config);
  return code;
};

const loadMomentLocale = async (
  locale: string,
): Promise<string | undefined> => {
  const code = locale.toLowerCase();
  try {
    return await importLocale(code);
  } catch {
    try {
      return await importLocale(code.split('-')[0]);
    } catch {}
  }
};

export const getMomentLocale = () => RESOLVED_MOMENT_LOCALE;

export const getMessagesForLocale = async (
  locale: string,
): Promise<Record<TranslationsKeys, string>> => {
  const [translations, momentLocale] = await Promise.all([
    import(`./translations/${locale}.json`),
    loadMomentLocale(locale),
  ]);
  RESOLVED_MOMENT_LOCALE = momentLocale ?? 'en';
  return translations.default;
};

export type CustomFormattedMessageProps = {
  values?: BaseFormattedMessageProps['values'];
  id: TranslationsKeys;
};

export const CustomFormattedMessage = ({
  id,
  values,
}: CustomFormattedMessageProps) => {
  return id ? (
    <FormattedMessage
      id={id}
      values={{
        ...values,
        br: <br />,
        i: (v) => <i>{v}</i>,
        b: (v) => <b>{v}</b>,
        u: (v) => <u>{v}</u>,
      }}
    />
  ) : (
    <></>
  );
};

type FormatMessageArgs = Parameters<IntlFormatters['formatMessage']>;

export const useCustomIntl = () => {
  const intl = useIntl();

  const customFormatMessage = (
    descriptor: FormatMessageArgs[0] & {
      id: TranslationsKeys;
    },
    values?: CustomFormattedMessageProps['values'],
    options?: FormatMessageArgs[2],
  ) => intl.formatMessage(descriptor, values, options).toString();

  return {
    ...intl,
    formatMessage: customFormatMessage,
  };
};

export const dateFormat: Intl.DateTimeFormatOptions = {
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

export const yearMonthFormat: Intl.DateTimeFormatOptions = {
  month: 'numeric',
  year: 'numeric',
};

export const CustomFormattedDate = ({ value }: { value?: number | string }) => {
  return (
    <FormattedDate
      value={value}
      {...dateFormat}
    />
  );
};
